/**
 * useShellScenes - the stage and the wings (v8.337.0).
 *
 * Every member listens to `shared/stage` for the title card and the
 * curtain; the GM alone holds the wings. The verbs take the live table as it
 * stands in this render and write in the order `useScenes` documents - park
 * before strike, tokens before the roster, the stage document last.
 *
 * Returns `{ stageSync, scenes }`. Extracted from DeathWatchSimulator.
 */
import { useMemo } from 'react';
import * as multiplayer from '../multiplayer';
import * as settings from '../engine/settings/resolve';
import useStage from './useStage';
import useScenes from './useScenes';

/** The GM's hidden figures as a park records them: alias, kept initiative, ever public (plan map-display, G1). */
function useHiddenMarks(figures) {
  return useMemo(() => {
    if (!figures?.size) return {};
    const marks = [...figures.values()].map((f) => [
      f.id,
      { alias: f.alias, initiative: f.initiative, bornHidden: f.bornHidden },
    ]);
    return Object.fromEntries(marks);
  }, [figures]);
}

/**
 * @param {object} args - sessionId, isGm, roster, setRoster, tokens,
 *   writeToken, removeToken, removeCombatant, terrain, setTerrain,
 *   deployment, setDeployment, arrivals, setArrivals, turnTracker, permissions, avoidOnDeploy,
 *   pendingDefences, narrateRoom, resetHistory, hidden (useHiddenFigures).
 *
 * Over the length target on purpose: it maps the shell's names onto
 * `useScenes`' - a declarative binding list.
 */
export default function useShellScenes(args) {
  const { sessionId, turnTracker, permissions } = args;
  const stageSync = useStage({ sessionId, mp: multiplayer });
  const hiddenMarks = useHiddenMarks(args.hidden?.figures);
  const scenes = useScenes({
    sessionId,
    isGm: args.isGm,
    mp: multiplayer,
    roster: args.roster,
    setRoster: args.setRoster,
    tokens: args.tokens,
    writeToken: args.writeToken,
    removeToken: args.removeToken,
    removeCombatant: args.removeCombatant,
    terrain: args.terrain,
    setTerrain: args.setTerrain,
    deployment: args.deployment,
    setDeployment: args.setDeployment,
    arrivals: args.arrivals,
    setArrivals: args.setArrivals,
    trackerState: turnTracker.trackerState,
    imposeTrackerState: turnTracker.imposeTrackerState,
    participantFor: turnTracker.participantFor,
    admit: {
      arrival: settings.resolveInitiativeArrivals(permissions),
      tieBreak: settings.resolveInitiativeTieBreak(permissions),
    },
    avoid: args.avoidOnDeploy,
    // Optional-chained: a bare render (the component tests) has no defence map.
    pendingPrompts: args.pendingDefences?.size ?? 0,
    narrate: args.narrateRoom,
    resetHistory: args.resetHistory,
    setPhase: args.setPhase,
    stage: stageSync.stage,
    setStage: stageSync.setStage,
    // Hidden figures park like anyone (the wings are the GM's) and are hidden again on the way back.
    hiddenMarks,
    restoreHidden: args.hidden?.restore,
  });
  return { stageSync, scenes };
}
